import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { ErrorPanel } from '../../components/ErrorPanel';
import { Card, SelectField, Spinner } from '../../components/ui';
import { BASE, DeviceHistory, keys } from './api';
import { HourChart } from './Charts';
import type { Point } from './Charts';

const RANGES = [1, 7, 14, 30] as const;

const avg = (xs: number[]): number => (xs.length ? Math.round(xs.reduce((a, b) => a + b, 0) / xs.length) : 0);

/** 30-day history (graphs) and the uptime figure for one device. */
export function HistoryCard({ deviceId }: { deviceId: string }) {
  const [days, setDays] = useState<number>(7);
  const q = useQuery({
    queryKey: keys.history(deviceId, days),
    queryFn: () => api.get(`${BASE}/${deviceId}/history`, { query: { days }, schema: DeviceHistory }),
  });
  const h = q.data;
  const series = (pick: (x: NonNullable<typeof h>['hours'][number]) => number | null): Point[] => (h ? h.hours.map((x) => ({ at: x.hour, value: pick(x) })) : []);
  const nums = (pts: Point[]): number[] => pts.flatMap((p) => (p.value === null ? [] : [p.value]));

  let body;
  if (q.isPending) body = <Spinner />;
  else if (q.isError) body = <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />;
  else if (!h || h.hours.length === 0) body = <p className="text-sm text-slate-600">{t('devicesUi.history.empty')}</p>;
  else {
    const online = series((x) => x.onlineMinutes);
    const battery = series((x) => x.batteryAvg);
    const lowest = h.hours.flatMap((x) => (x.batteryMin === null ? [] : [x.batteryMin]));
    const wifi = series((x) => x.wifiAvg);
    const signIns = series((x) => x.signIns);
    const signInTotal = nums(signIns).reduce((a, b) => a + b, 0);
    body = (
      <div className="grid gap-6 lg:grid-cols-2">
        <HourChart
          kind="bars"
          title={t('devicesUi.chart.online')}
          summary={t('devicesUi.chart.summary.online', { hours: Math.round(nums(online).reduce((a, b) => a + b, 0) / 60) })}
          points={online}
          from={h.from}
          to={h.to}
          max={60}
        />
        <HourChart
          kind="line"
          title={t('devicesUi.chart.battery')}
          summary={t('devicesUi.chart.summary.battery', { avg: avg(nums(battery)), min: lowest.length ? Math.min(...lowest) : 0 })}
          points={battery}
          from={h.from}
          to={h.to}
          max={100}
        />
        <HourChart
          kind="line"
          title={t('devicesUi.chart.wifi')}
          summary={t('devicesUi.chart.summary.wifi', { avg: avg(nums(wifi)) })}
          points={wifi}
          from={h.from}
          to={h.to}
          max={100}
        />
        <HourChart
          kind="bars"
          title={t('devicesUi.chart.signIns')}
          summary={t('devicesUi.chart.summary.signIns', { count: signInTotal })}
          points={signIns}
          from={h.from}
          to={h.to}
          max={Math.max(5, ...nums(signIns))}
        />
      </div>
    );
  }

  return (
    <Card title={t('devicesUi.history.title')} className="mb-6">
      <div className="mb-4 flex flex-wrap items-end gap-6">
        <SelectField label={t('devicesUi.history.range')} value={String(days)} onChange={(e) => setDays(Number(e.target.value))}>
          {RANGES.map((d) => (
            <option key={d} value={d}>
              {d === 1 ? t('devicesUi.history.day') : t('devicesUi.history.days', { days: d })}
            </option>
          ))}
        </SelectField>
        {h ? (
          <p className="text-sm">
            <span className="text-slate-600">{t('devicesUi.history.uptime')}: </span>
            <strong className="text-lg">{t('devicesUi.history.uptimeValue', { percent: h.uptimePercent.toFixed(1) })}</strong>
          </p>
        ) : null}
      </div>
      {body}
    </Card>
  );
}
